import {Store} from 'common/report/stores/store';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {ContentType, Dashboard, DashboardCard} from 'common/report/dashboards/types';
import type {BookmarksMap, BookmarkStore} from 'common/report/stores/bookmarks';

export interface DashboardsMap {
  [dashboardId: string]: Dashboard;
}

export type DashboardStore = Store<DashboardsMap>;

// store for individual dashboards
export function createDashboardStore(): Store<DashboardsMap> {
  return new Store<DashboardsMap>({
    apiClient: dashboardsClient,
    defaultValue: {},
    name: `dashboard`,
    transform: {
      index: (dashboard) => ({[String(dashboard.id)]: dashboard}),
    },
  });
}

/**
 * Sets up value sharing such that when the DashboardStore's dashboard entity values are loaded/updated,
 * the bookmark entity values inside the dashboard are automatically loaded into the BookmarkStore offline.
 */
export function shareDashboardBookmarks(dashboardStore: DashboardStore, bookmarkStore: BookmarkStore): void {
  dashboardStore.shareValues(
    bookmarkStore,
    (valueUpdate): Array<{key: string; valueUpdate: BookmarksMap}> => {
      const sharedBookmarkValues: Array<{key: string; valueUpdate: BookmarksMap}> = [];
      for (const dashboardId in valueUpdate) {
        const dashboard = valueUpdate[dashboardId];
        const cards =
          dashboard.cards?.filter((c): c is DashboardCard =>
            [ContentType.Report, ContentType.ReportLink].includes(c.type),
          ) ?? [];
        const cardBookmarks = cards.map((c) => c.bookmark).filter((b) => b);
        const bookmarks = dashboard.bookmarks ?? [];
        sharedBookmarkValues.push(
          ...[...bookmarks, ...cardBookmarks].map((b) => {
            const key = b.id.toString();
            return {key, valueUpdate: {[key]: b}};
          }),
        );
      }
      return sharedBookmarkValues;
    },
  );
}
